import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import styles from "./RentsNoticeToQuitUploadFile.module.css";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const RentsNoticeToQuitUploadFile = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    file: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [rent, setRent] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [hasNoticeToQuit, setHasNoticeToQuit] = useState(true); // Assume true initially

  useEffect(() => {
    loadRentData();
  }, [rentId]);

  const loadRentData = async () => {
    try {
      setLoading(true);
      
      // Call real backend API
      const response = await fetch(
        `${API_URL}/api/rents/${rentId}/notice-to-quit/uploadFile`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.rent) {
          setRent(data.rent);
          setGuardian(data.rent.guardian);
          // Check if NTQ exists (rent should have notice_to_quit property)
          setHasNoticeToQuit(data.rent.notice_to_quit !== null && data.rent.notice_to_quit !== undefined);
        } else {
          toast.error(data.message || "Failed to load rent data");
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to load rent data");
      }
    } catch (error) {
      console.error("Error loading rent data:", error);
      toast.error("Failed to load rent data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      file: file
    }));
    
    // Clear file error when user selects a file
    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.file) {
      newErrors.file = "Document is required";
    } else {
      // Check file type
      const allowedTypes = ['doc', 'docx', 'txt', 'pdf'];
      const fileExtension = formData.file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        newErrors.file = "File must be a doc, docx, txt, or pdf file";
      }
      
      // Check file size (max 10MB)
      if (formData.file.size > 10 * 1024 * 1024) {
        newErrors.file = "File size must be less than 10MB";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('file', formData.file);
      
      // Call real API
      const response = await fetch(
        `${API_URL}/api/rents/${rentId}/notice-to-quit/storeFile`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: submitData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "File uploaded successfully!");
        // Redirect to notice to quit list (matches Laravel behavior)
        navigate('/rents/notice_to_quit');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || "Failed to upload file. Please try again.";
        
        // If no NTQ exists, update state to show warning banner
        if (errorMessage.includes("no notice to quit")) {
          setHasNoticeToQuit(false);
          toast.error("Please create a Notice to Quit first before uploading a file.");
        } else {
          toast.error(errorMessage);
        }
      }
      
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !rent) {
    return <Loader />;
  }

  if (!rent) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.alertDanger}>
          The rent doesn't exist!
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Breadcrumbs */}
        <div className={styles.breadcrumbRow}>
          <ol className={styles.breadcrumb}>
            <li>
              <Link to="/dashboard" className={styles.breadcrumbLink}>
                Dashboard
              </Link>
            </li>
            <li>
              <Link to="/guardians" className={styles.breadcrumbLink}>
                Guardians
              </Link>
            </li>
            <li>
              <Link 
                to={`/guardians/${guardian?.id}`} 
                className={styles.breadcrumbLink}
              >
                {guardian?.user?.fullName} ({guardian?.unique_guardian_number})
              </Link>
            </li>
            <li className={styles.active}>
              Documents
            </li>
          </ol>
        </div>

        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panelForm}>
              <div className={styles.panelHeading}>
                <h3 className={styles.panelTitle}>Upload document</h3>
              </div>
              <div className={styles.panelBody}>
                {!hasNoticeToQuit && (
                  <div className={styles.alertWarning} style={{
                    padding: '15px',
                    marginBottom: '20px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404'
                  }}>
                    <strong>Notice:</strong> A Notice to Quit record must be created before uploading a file.
                    <br />
                    <button
                      onClick={() => navigate(`/rents/${rentId}/notice-to-quit/create`)}
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        backgroundColor: '#1e3a8a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Create Notice to Quit First
                    </button>
                  </div>
                )}
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <fieldset className={styles.formHorizontal}>
                    <div className={styles.formGroup}>
                      <label 
                        htmlFor="file" 
                        className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                      >
                        Document
                      </label>
                      <div className={styles.colSm5}>
                        <div className={styles.inputImage}>
                          <input
                            type="file"
                            id="file"
                            name="file"
                            className={styles.formControl}
                            onChange={handleFileChange}
                            accept=".doc,.docx,.txt,.pdf"
                            disabled={!hasNoticeToQuit}
                          />
                          {errors.file && (
                            <div className={styles.textDanger}>
                              {errors.file}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </fieldset>

                  <div className={`${styles.formGroup} ${styles.textRight}`} style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <button 
                      type="submit" 
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                      disabled={loading || !hasNoticeToQuit}
                    >
                      {loading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentsNoticeToQuitUploadFile; 